import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import type { PriceEnvelopeDay, PriceHistoryPoint, ShopListing } from "@/types";
import { getShopLogoUrl } from "@/utils/shopLogos";
import { shopClickUrl } from "@/lib/shopClickUrl";
import { PriceTrendChart } from "./PriceTrendChart";

interface Props {
  points: PriceHistoryPoint[];
  envelope: PriceEnvelopeDay[];
  shopListings: ShopListing[];
  trackingSince: string | null;
}

function formatEuro(n: number) {
  return `${n.toFixed(2)} €`;
}

function formatEuroNumber(n: number) {
  return n.toFixed(2);
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("lt-LT", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function median(nums: number[]) {
  if (nums.length === 0) return 0;
  const sorted = [...nums].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

// Picks the right verdict-line translation and its interpolation params.
// Returns null when we don't have enough data to say anything useful.
function verdict(
  points: PriceHistoryPoint[],
  shopListings: ShopListing[],
  t: TFunction,
): string | null {
  const shopPoints = points.filter((p) => p.source === "shop");
  if (shopPoints.length < 2 && shopListings.length === 0) return null;

  const allTimeLow = shopPoints.length > 0
    ? Math.min(...shopPoints.map((p) => p.price))
    : null;
  const currentLowest = shopListings
    .filter((s) => s.price != null)
    .reduce<number | null>((min, s) => (min == null || (s.price as number) < min ? (s.price as number) : min), null);

  const now = Date.now();
  const cutoff = now - 30 * 24 * 60 * 60 * 1000;
  const recent = shopPoints.filter((p) => new Date(p.date).getTime() >= cutoff);

  if (currentLowest != null && allTimeLow != null && Math.abs(currentLowest - allTimeLow) < 0.01) {
    return t("price_history.verdict_all_time_low", { price: formatEuroNumber(currentLowest) });
  }

  if (recent.length >= 2) {
    const min = Math.min(...recent.map((p) => p.price));
    const max = Math.max(...recent.map((p) => p.price));
    const range = max - min;
    if (range < 1) {
      return t("price_history.verdict_stable_30d", { price: formatEuroNumber(min) });
    }
    if (currentLowest != null && currentLowest <= min + 0.5) {
      return t("price_history.verdict_good_moment", { price: formatEuroNumber(currentLowest) });
    }
    return t("price_history.verdict_range_30d", {
      min: formatEuroNumber(min),
      max: formatEuroNumber(max),
    });
  }

  if (allTimeLow != null) {
    return t("price_history.verdict_historical_low", { price: formatEuroNumber(allTimeLow) });
  }

  return null;
}

export function PriceHistory({ points, envelope, shopListings, trackingSince }: Props) {
  const { t } = useTranslation();
  if (points.length === 0 && shopListings.length === 0) return null;

  const aggregatorOnly =
    points.length > 0 && points.every((p) => p.shopName === "BoardGamePrices");

  return (
    <section className="mt-12">
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-2xl font-bold text-gray-900">
          {t("price_history.section_heading")}
        </h2>
        {trackingSince && (
          <p className="text-xs text-gray-500">
            {t("price_history.tracking_since", { date: formatDate(trackingSince) })}
          </p>
        )}
      </div>

      {aggregatorOnly && (
        <p className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {t("price_history.aggregator_only")}
        </p>
      )}

      <PriceSummary points={points} shopListings={shopListings} />

      {(() => {
        const v = verdict(points, shopListings, t);
        return v ? (
          <p className="mb-3 inline-flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-1.5 text-sm font-medium text-emerald-800">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {v}
          </p>
        ) : null;
      })()}

      <PriceTrendChart envelope={envelope} />
    </section>
  );
}

function PriceSummary({
  points,
  shopListings,
}: {
  points: PriceHistoryPoint[];
  shopListings: ShopListing[];
}) {
  const { t } = useTranslation();

  const lowestCurrent = useMemo(() => {
    const withPrice = shopListings.filter(
      (s): s is ShopListing & { price: number } => s.price != null,
    );
    if (withPrice.length === 0) return null;
    return withPrice.reduce((min, s) => (s.price < min.price ? s : min));
  }, [shopListings]);

  const recentStats = useMemo(() => {
    const now = Date.now();
    const cutoff = now - 30 * 24 * 60 * 60 * 1000;
    const recent = points.filter((p) => new Date(p.date).getTime() >= cutoff);
    if (recent.length === 0) return null;
    const shops = new Set(
      recent.filter((p) => p.source === "shop").map((p) => p.shopName),
    );
    return {
      count: recent.length,
      median: median(recent.map((p) => p.price)),
      shopCount: shops.size,
    };
  }, [points]);

  if (!lowestCurrent && !recentStats) return null;

  return (
    <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {lowestCurrent && (
        <a
          href={shopClickUrl(lowestCurrent.id)}
          target="_blank"
          rel="noopener noreferrer"
          className="group flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3 transition-colors hover:border-primary-200"
        >
          {getShopLogoUrl(lowestCurrent.shopName) ? (
            <img
              src={getShopLogoUrl(lowestCurrent.shopName)!}
              alt={lowestCurrent.shopName}
              className="h-9 w-9 shrink-0 rounded-lg object-contain"
            />
          ) : (
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-sm font-bold text-gray-600">
              {lowestCurrent.shopName.charAt(0).toUpperCase()}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="text-xs text-gray-500">
              {t("price_history.summary_lowest_now")}
            </p>
            <p className="text-lg font-bold text-gray-900">
              {formatEuro(lowestCurrent.price)}
            </p>
            <p className="truncate text-xs text-gray-500 group-hover:text-primary-700">
              {lowestCurrent.shopName}
            </p>
          </div>
        </a>
      )}

      {recentStats && (
        <div className="rounded-xl border border-gray-200 bg-white p-3">
          <p className="text-xs text-gray-500">
            {t("price_history.summary_median_30d")}
          </p>
          <p className="text-lg font-bold text-gray-900">
            {formatEuro(recentStats.median)}
          </p>
          <p className="text-xs text-gray-500">
            {t("price_history.summary_observations", { count: recentStats.count })}
            {recentStats.shopCount > 0 &&
              ` · ${t("price_history.summary_shops", { count: recentStats.shopCount })}`}
          </p>
        </div>
      )}
    </div>
  );
}
